import React, {Component} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import DateUtils from 'utils/date';
import TimeUtils from 'utils/time';
import DatePicker from '@spothero/ui/date-picker';
import DateTimeRangePicker from '@spothero/ui/date-time-range-picker';
import Label from '@spothero/ui/label';
import Tooltip from '@spothero/ui/tooltip';
import withConfig from 'config/withConfig';

const defaultTimePickerFormat = 'h:mm A';

class SearchDateTimePickers extends Component {
    static propTypes = {
        isAdmin: PropTypes.bool.isRequired,
        isMobile: PropTypes.bool.isRequired,
        isMonthly: PropTypes.bool.isRequired,
        timezone: PropTypes.string.isRequired,
        starts: PropTypes.string,
        ends: PropTypes.string,
        startDateLabel: PropTypes.element,
        endDateLabel: PropTypes.element,
        monthlyStartDateLabel: PropTypes.element,
        displayDateFormat: PropTypes.string.isRequired,
        standardTimeFormat: PropTypes.string.isRequired,
        useCalendarWithShim: PropTypes.bool,
        onChange: PropTypes.func.isRequired,
    };
    static defaultProps = {
        starts: null,
        ends: null,
        startDateLabel: <Label>Enter After</Label>,
        endDateLabel: <Label>Exit Before</Label>,
        monthlyStartDateLabel: (
            <Label className="monthly-label">Monthly Parking Starting</Label>
        ),
    };

    constructor(props) {
        super(props);

        const {isMobile, starts, ends, endDateLabel} = props;

        this._endDateLabel = endDateLabel ? (
            endDateLabel
        ) : !isMobile ? (
            <Label>to</Label>
        ) : null;

        this.state = {
            startTooltipMessage: null,
            start: starts ? moment(starts) : moment(),
            end: ends ? moment(ends) : moment(),
            isValidStart: true,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {isMonthly} = nextProps;
        const {startTooltipMessage} = prevState;

        if (isMonthly && startTooltipMessage) {
            return {
                startTooltipMessage: null,
            };
        }

        return null;
    }

    componentDidUpdate(prevProps, prevState) {
        const {start, end, isValidStart} = this.state;
        const {
            start: prevStart,
            end: prevEnd,
            isValidStart: prevIsValidStart,
        } = prevState;

        if (
            !prevStart.isSame(start) ||
            !prevEnd.isSame(end) ||
            prevIsValidStart !== isValidStart
        ) {
            this.props.onChange({start, end, isValidStart});
        }
    }

    _onTransientChange = ({startDate, startTime, endDate, endTime}) => {
        const {isAdmin, timezone} = this.props;
        const starts = DateUtils.combineDateAndTime(
            DateUtils.getAsMoment(startDate),
            TimeUtils.getAsMoment(startTime)
        );
        const ends = DateUtils.combineDateAndTime(
            DateUtils.getAsMoment(endDate),
            TimeUtils.getAsMoment(endTime)
        );
        const newState = {
            startTooltipMessage: null,
            start: starts,
            end: ends,
            isValidStart: true,
        };

        if (!isAdmin) {
            const {isNewStart} = TimeUtils.checkStaleStart(
                starts.clone(),
                timezone
            );

            if (isNewStart) {
                newState.startTooltipMessage =
                    'Cannot search for more than 30 minutes in the past.';
                newState.isValidStart = false;
            }
        }

        this.setState(newState);
    };

    _onMonthlyChange = selectedDate => {
        const selectedDateMoment = DateUtils.getAsMoment(selectedDate);
        const start = selectedDateMoment.clone().hour(moment().hour());

        this.setState({
            start,
            end: start.clone().add(3, 'hours'),
            isValidStart: true,
        });
    };

    _onStartTooltipHidden = () => {
        this.setState({
            startTooltipMessage: null,
        });
    };

    _renderTransient() {
        const {
            startDateLabel,
            displayDateFormat,
            standardTimeFormat,
            useCalendarWithShim,
        } = this.props;
        const {start, end} = this.state;

        return (
            <DateTimeRangePicker
                startId="SearchDateTimePickers-transient-start"
                endId="SearchDateTimePickers-transient-end"
                className="SearchDateTimePickers-transient"
                startDateLabel={startDateLabel}
                endDateLabel={this._endDateLabel}
                startDateDisplayFormat={DateUtils.getTransientDateDisplayFormat}
                startTimeDisplayFormat={defaultTimePickerFormat}
                startDate={start.format(displayDateFormat)}
                startTime={start.format(standardTimeFormat)}
                endDateDisplayFormat={DateUtils.getTransientDateDisplayFormat}
                endTimeDisplayFormat={defaultTimePickerFormat}
                endDate={end.format(displayDateFormat)}
                endTime={end.format(standardTimeFormat)}
                endTimeBump={180}
                useTetherShim={useCalendarWithShim}
                autoBumpInvalidEnabled
                onStartChange={this._onTransientChange}
                onEndChange={this._onTransientChange}
            />
        );
    }

    _renderMonthly() {
        const {monthlyStartDateLabel, displayDateFormat} = this.props;
        const {start} = this.state;

        return (
            <div className="SearchDateTimePickers-monthly">
                {monthlyStartDateLabel}
                <DatePicker
                    id="SearchDateTimePickers-monthly-start"
                    useIcon
                    calendarClassName="calendar-start"
                    selectedDate={start.format(displayDateFormat)}
                    displayFormat={DateUtils.getMonthlyDateDisplayFormat}
                    onChange={this._onMonthlyChange}
                    disablePast
                />
            </div>
        );
    }

    render() {
        const {startTooltipMessage} = this.state;

        return (
            <div className="SearchDateTimePickers">
                {this.props.isMonthly
                    ? this._renderMonthly()
                    : this._renderTransient()}
                {startTooltipMessage && (
                    <Tooltip
                        triggerSelector=".SearchDateTimePickers-transient .DateTimePicker-start select"
                        position="bottom"
                        updateOnMove
                        onHidden={this._onStartTooltipHidden}
                    >
                        {startTooltipMessage}
                    </Tooltip>
                )}
            </div>
        );
    }
}

export default withConfig([
    'displayDateFormat',
    'isMobile',
    'standardTimeFormat',
])(SearchDateTimePickers);
